// Auto-generated by scripts/split_calculators.py — keeps deterministic oilfield formulas in per-category modules.
import { round, num, positive, toRad } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, toRad };

// ===== Category: cement =====
// --- Cementing ---
// =========================================================================
// CEMENTING
// =========================================================================

export function cementSlurryVolume({ casingOd, holeId, length, excess }) {
  const Co = positive(num(casingOd, "Casing OD"), "Casing OD");
  const Hi = positive(num(holeId, "Hole ID"), "Hole ID");
  if (Co >= Hi) throw new Error("Casing OD must be less than hole ID");
  const L = positive(num(length, "Length"), "Length");
  const ex = num(excess || 0, "Excess %");
  const ann = ((Hi * Hi - Co * Co) / 1029.4) * L;
  const total = ann * (1 + ex / 100);
  return {
    result: round(total, 2),
    unit: "bbl",
    formula: "V = ((Dh² − Dc²) / 1029.4) × L × (1 + excess%)",
    steps: [
      `Annular V = (${Hi}² − ${Co}²)/1029.4 × ${L} = ${round(ann, 2)} bbl`,
      `With ${ex}% excess: V = ${round(total, 2)} bbl`,
    ],
    inputs: { "Casing OD (in)": Co, "Hole ID (in)": Hi, "Length (ft)": L, "Excess (%)": ex },
  };
}

export function sacksRequired({ slurryVolumeBbl, yieldFt3Sx }) {
  const V = positive(num(slurryVolumeBbl, "Slurry Volume"), "Slurry Volume");
  const Y = positive(num(yieldFt3Sx, "Cement Yield"), "Cement Yield");
  const V_ft3 = V * 5.615;
  const sx = V_ft3 / Y;
  return {
    result: round(sx, 0),
    unit: "sx",
    formula: "Sacks = (V_bbl × 5.615) / Yield",
    steps: [
      `V = ${V} bbl = ${round(V_ft3, 2)} ft³`,
      `Sacks = ${round(V_ft3, 2)} / ${Y} = ${round(sx, 0)} sx`,
    ],
    inputs: { "Slurry Volume (bbl)": V, "Yield (ft³/sx)": Y },
  };
}

export function cementYield({ cementWeight, additivesWeight, slurryDensity }) {
  // Yield (ft³/sx) = total mass / slurry density (in lb/ft³)
  const C = positive(num(cementWeight, "Cement (lb/sx)"), "Cement");
  const A = num(additivesWeight || 0, "Additives (lb/sx)");
  if (A < 0) throw new Error("Additives must be ≥ 0");
  const Wppg = positive(num(slurryDensity, "Slurry Density"), "Slurry Density");
  const Wpcf = Wppg * 7.48; // ppg→lb/ft³
  const yieldFt3 = (C + A) / Wpcf;
  return {
    result: round(yieldFt3, 3),
    unit: "ft³/sx",
    formula: "Yield = (C + Add) / (Slurry ppg × 7.48)",
    steps: [
      `Mass/sx = ${C} + ${A} = ${C + A} lb`,
      `Slurry density = ${Wppg} ppg = ${round(Wpcf, 2)} lb/ft³`,
      `Yield = ${C + A} / ${round(Wpcf, 2)} = ${round(yieldFt3, 3)} ft³/sx`,
    ],
    inputs: { "Cement (lb/sx)": C, "Additives (lb/sx)": A, "Slurry (ppg)": Wppg },
  };
}

export function mixWaterRequired({ sacks, gallonsPerSack }) {
  const S = positive(num(sacks, "Sacks"), "Sacks");
  const G = positive(num(gallonsPerSack, "Gal/Sack"), "Gal/Sack");
  const totalGal = S * G;
  const totalBbl = totalGal / 42;
  return {
    result: round(totalBbl, 2),
    unit: "bbl",
    formula: "Mix Water = Sx × gal/sx ÷ 42",
    steps: [
      `Total = ${S} × ${G} = ${totalGal} gal`,
      `= ${round(totalBbl, 2)} bbl`,
    ],
    inputs: { Sacks: S, "Gal/Sack": G },
    extras: { "Total (gal)": totalGal },
  };
}

export function spacerVolume({ holeId, casingOd, lengthCovered }) {
  const Hi = positive(num(holeId, "Hole ID"), "Hole ID");
  const Co = positive(num(casingOd, "Casing OD"), "Casing OD");
  if (Co >= Hi) throw new Error("Casing OD must be less than hole ID");
  const L = positive(num(lengthCovered, "Length Covered"), "Length Covered");
  const v = ((Hi * Hi - Co * Co) / 1029.4) * L;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "V_spacer = ((Dh² − Dc²) / 1029.4) × L",
    steps: [
      `(${Hi}² − ${Co}²)/1029.4 × ${L} = ${round(v, 2)} bbl`,
    ],
    inputs: { "Hole ID": Hi, "Casing OD": Co, "Length (ft)": L },
  };
}

export function displacementVolumeCement({ casingId, plugDepth }) {
  const I = positive(num(casingId, "Casing ID"), "Casing ID");
  const L = positive(num(plugDepth, "Plug Depth"), "Plug Depth");
  const v = (I * I / 1029.4) * L;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "Disp = (ID² / 1029.4) × L",
    steps: [`(${I}²/1029.4) × ${L} = ${round(v, 2)} bbl`],
    inputs: { "Casing ID (in)": I, "Plug Depth (ft)": L },
  };
}

// --- v1.4 — Cementing additions ---
// ----- Cementing additions -----

export function annularCementVolume({ annularCapacity, intervalLength, excess }) {
  const cap = positive(num(annularCapacity, "Annular Capacity"), "Annular Capacity");
  const L = positive(num(intervalLength, "Interval Length"), "Interval Length");
  const ex = num(excess || 0, "Excess");
  if (ex < 0) throw new Error("Excess must be ≥ 0");
  const base = cap * L;
  const total = base * (1 + ex / 100);
  return {
    result: round(total, 2),
    unit: "bbl",
    formula: "V = Annular Capacity × Length × (1 + excess%)",
    steps: [
      `Base = ${cap} × ${L} = ${round(base, 2)} bbl`,
      `With ${ex}% excess → ${round(total, 2)} bbl`,
    ],
    inputs: { "Annular Cap (bbl/ft)": cap, "Length (ft)": L, "Excess (%)": ex },
  };
}

export function topOfCement({ cementVolume, annularCapacity, shoeDepth }) {
  const V = positive(num(cementVolume, "Cement Volume"), "Cement Volume");
  const cap = positive(num(annularCapacity, "Annular Capacity"), "Annular Capacity");
  const D = positive(num(shoeDepth, "Shoe Depth"), "Shoe Depth");
  const fillLen = V / cap;
  const toc = D - fillLen;
  return {
    result: round(toc, 0),
    unit: "ft (estimated TOC)",
    formula: "Fill = V / cap ;  TOC = Shoe − Fill",
    steps: [
      `Fill length = ${V} / ${cap} = ${round(fillLen, 1)} ft`,
      `TOC = ${D} − ${round(fillLen, 1)} = ${round(toc, 0)} ft`,
    ],
    inputs: { "Cement V (bbl)": V, "Annular Cap (bbl/ft)": cap, "Shoe (ft)": D },
    extras: { "Fill Length (ft)": round(fillLen, 1) },
  };
}


